import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Elements } from '@recurly/react-recurly';

import { useModal } from '@nebula/modal';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  ChangeBillingDateStage,
  changeBillingDateTransition,
  isFlutterApp,
  MobileLocations,
  navigateTo,
} from '@ecs-uk/web-common-mobile-view-helpers';
import { ReviewAndPayContainer, BackLink, ReviewAndPayContent } from './index.styles';

import OneTimePayment from './oneTimePayment';
import ChangedDates from './changedDates';
import StepIndicator from './stepIndicator';
import PageTitle from './pageTitle';
import ActionButtons from './actionButtons';
import CancelBillingDateChangeDialog from '../../../components/CancelBillingDateChangeDialog';
import ChangeBillingDateFailure from '../change-billing-date-failure';
import {
  publishCancelClick,
  publishReviewAndPayBillDateChangeData,
  publishOneTimePaymentCalculationFailure,
  publishConfirmPurchaseClick,
  trackPaymentMethodTokenizationResult,
} from '../../../helpers/changeBillingDate/analytics';
import { calculateDaysBetween } from '../../../helpers/dateCalculations';
import { PaymentContext } from '../../../components/PaymentOrchestrator/PaymentContextProvider';
import changeBillingPaymentMethodTransition from '../../../components/PaymentOrchestrator/transitions/changeBillingPaymentMethodTransition';
import {
  PAYMENT_ERROR_GROUPS,
  PAYMENT_ERROR_TYPES,
} from '../../../components/PaymentOrchestrator/constants/paymentErrors';
import getOneTimePaymentValue from '../../../actions/billing/getOneTimePaymentValue';
import usePageView from '../../../hooks/usePageView';
import Loader from './loader';
import useTrackUpsellStage from '../../../components/PaymentOrchestrator/hooks/useTrackUpsellStage';
import { PAYMENT_LOG_STEPS } from '../../../constants/paymentLogSteps';

const PAGE_NAME = 'ProdMoveReviewAndPayPage';

export const ReviewAndPayIndex = () => {
  const hasLoaded = useSelector(state => state.oneTimePayment?.fetched);
  const dispatch = useDispatch();
  const { isWebview } = useContext(PaymentContext);

  useEffect(() => {
    dispatch(getOneTimePaymentValue());
  }, [dispatch]);

  useEffect(() => {
    if (isWebview) {
      changeBillingDateTransition({ stage: ChangeBillingDateStage.REVIEW });
    }
  }, [isWebview]);

  usePageView(PAGE_NAME);

  return !hasLoaded ? <Loader text="Calculating payment" /> : <ReviewAndPay />;
};

const ReviewAndPay = () => {
  const paymentContext = useContext(PaymentContext);
  const { isWebview } = paymentContext;
  const { isShowing, toggle } = useModal();
  const history = useHistory();
  const dispatch = useDispatch();
  const cvvSubmitHandlerRef = React.useRef(null);
  const [cvvSubmitHandler, setCvvSubmitHandler] = useState(null);
  const [cvvState, setCvvState] = useState({ isValid: false, isSubmitting: false });

  const handleSetCvvSubmitHandler = useCallback(handler => {
    cvvSubmitHandlerRef.current = handler;
    // Store the function itself in state. Passing it directly would execute it as a state updater.
    setCvvSubmitHandler(() => handler);
  }, []);
  const [hasTokenizationError, setHasTokenizationError] = useState(false);

  const { nextBillingDate, eligibility, selectedDate, planPrice, oneTimePaymentValue, hasLoaded } = useSelector(
    state => ({
      nextBillingDate: state.billing?.nextBillingDate,
      eligibility: state.billing?.dateChangeEligibility.data,
      selectedDate: state.billingDateSelection?.selectedBillDate,
      planPrice: state.billing?.signupPrice,
      oneTimePaymentValue: state.oneTimePayment?.price,
      hasLoaded: state.oneTimePayment?.fetched,
    })
  );

  const trackUpsellStage = useTrackUpsellStage(true);
  const currentBillDate = dayjs(eligibility.earliestSelectableDate).subtract(1, 'day').toDate();
  const selectedDateFormatted = dayjs(selectedDate).format('D MMMM YYYY');
  const daysBetween = calculateDaysBetween(currentBillDate, selectedDate);

  const hasError = hasLoaded && oneTimePaymentValue === null;

  const handlePaymentMethodTokenization = useCallback(
    (token, paymentType) => {
      publishReviewAndPayBillDateChangeData({
        currentBillDate: dayjs(nextBillingDate).format('MM/DD/YYYY'),
        selectedBillDate: dayjs(selectedDate).format('MM/DD/YYYY'),
        adjustmentDays: daysBetween,
        oneTimePaymentAmountDue: oneTimePaymentValue,
      });

      publishConfirmPurchaseClick(true, paymentType);

      trackPaymentMethodTokenizationResult(true, paymentType);

      if (!paymentContext || !token) return;

      changeBillingPaymentMethodTransition(
        paymentContext,
        { newMethodToken: token.id, paymentType, selectedBillDate: selectedDate },
        undefined
      )(dispatch);
    },
    [nextBillingDate, selectedDate, daysBetween, oneTimePaymentValue, paymentContext, dispatch]
  );

  const handlePaymentMethodTokenizationError = useCallback(
    (error, paymentType) => {
      trackUpsellStage(PAYMENT_LOG_STEPS.PAYMENT_METHOD_TOKENIZATION_FAILED, {
        message: error.message,
        code: error.code,
        name: error.name,
        statusCode: error.statusCode,
        paymentMethod: paymentType,
      });
      publishConfirmPurchaseClick(false, paymentType);

      trackPaymentMethodTokenizationResult(false, paymentType);

      if (!paymentContext) return;

      paymentContext.clearPaymentErrors?.();
      paymentContext.raisePaymentError?.({
        message: error?.message,
        group: PAYMENT_ERROR_GROUPS.TOKENIZATION,
        type: PAYMENT_ERROR_TYPES.TOKENIZATION.GENERATING_PAYMENT_TOKEN,
      });
      if (error?.message) {
        setHasTokenizationError(true);
      }
    },
    [paymentContext]
  );

  const handleCancel = useCallback(() => {
    publishCancelClick('review_and_pay_page');
    const paymentAndBillingPath = `${isWebview ? '/mobile' : ''}/billing`;
    history.replace(paymentAndBillingPath);
  }, [history, isWebview]);

  const handleBackKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggle();
    }
  };

  useEffect(() => {
    if (!isWebview) {
      const timer = setTimeout(() => {
        const backLinkElement = document.querySelector('[data-back-link="true"]');
        if (backLinkElement) {
          backLinkElement.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isWebview]);

  if (hasError || hasTokenizationError) {
    return (
      <ChangeBillingDateFailure
        heading="We could not show your information"
        subheading="Please check your internet connection."
        onActionClick={() => {
          const paymentAndBillingPath = `${isWebview ? '/mobile' : ''}/billing`;
          history.replace(paymentAndBillingPath);
        }}
        onPageLoad={() => {
          publishOneTimePaymentCalculationFailure(dayjs(selectedDate).format('MM/DD/YYYY'));
        }}
        isWebview={isWebview}
      />
    );
  }

  return (
    <ReviewAndPayContainer isWebview={isWebview}>
      <Elements>
        <ReviewAndPayContent>
          {!isWebview && !cvvState.isSubmitting && (
            <BackLink
              id="review_and_pay_back"
              href="#"
              data-back-link="true"
              onClick={event => {
                event.preventDefault();
                toggle();
              }}
              onKeyDown={handleBackKeyDown}
              tabIndex={0}
            >
              Back
            </BackLink>
          )}
          <StepIndicator />
          <PageTitle />
          <ChangedDates disableEditLink={cvvState.isSubmitting} />
          <OneTimePayment
            planPrice={planPrice}
            daysBetween={daysBetween}
            oneTimePaymentValue={oneTimePaymentValue}
            selectedDateFormatted={selectedDateFormatted}
            onCardTokenization={handlePaymentMethodTokenization}
            onCardTokenizationError={handlePaymentMethodTokenizationError}
            registerCvvSubmitHandler={handleSetCvvSubmitHandler}
            onCvvStateChange={setCvvState}
          />{' '}
          <ActionButtons
            isWebview={isWebview}
            onCancel={() => {
              if (isWebview && isFlutterApp()) {
                navigateTo(MobileLocations.CHANGE_BILLING_DATE_CANCEL_MODAL);
                return;
              }
              toggle();
            }}
            onWalletAuthSuccess={handlePaymentMethodTokenization}
            onWalletAuthFailure={handlePaymentMethodTokenizationError}
            onPayAndChangeDate={cvvSubmitHandlerRef.current || cvvSubmitHandler}
            isTokenizing={cvvState.isSubmitting}
            isCvvValid={cvvState.isValid}
          />
        </ReviewAndPayContent>
      </Elements>
      <CancelBillingDateChangeDialog isShowing={isShowing} toggleHide={toggle} onYes={handleCancel} />
    </ReviewAndPayContainer>
  );
};

export default ReviewAndPay;
